// =============================================================
// Shared "legal document" shell primitives
// -------------------------------------------------------------
// Used by both src/pages/TermsConditions and src/pages/CookiePolicy
// so the two legal pages stay in lockstep: a sticky numbered
// table of contents + numbered section heads on cream paper.
//
// Type follows the rest of the app: Inter display heads via
// `font-sans` + extrabold, Manrope labels via `font-display`,
// Inter body. Tokens resolve against tailwind.config.js.
// =============================================================

import React from 'react';

// slug helper — stable ids for TOC anchors
export const slug = (s) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const pad = (n) => String(n).padStart(2, '0');

export const DocHero = ({ title }) => (
  <section className="bg-paper border-b border-line px-9 pt-[72px] pb-[52px]">
    <div className="mx-auto max-w-[1080px]">
      <h1 className="max-w-[16ch] font-sans font-extrabold leading-[0.95] tracking-[-0.02em] text-ink-strong text-[38px] sm:text-[54px] lg:text-[66px]">
        {title}
      </h1>
    </div>
  </section>
);

// `items` = [{ id, label }]
export const Toc = ({ items, ariaLabel = 'Índice' }) => (
  <nav className="lg:sticky lg:top-7" aria-label={ariaLabel}>
    <div className="mb-2 border-b border-line pb-3.5 font-display text-[11px] font-bold uppercase tracking-[0.16em] text-muted">
      {ariaLabel}
    </div>
    <ol className="m-0 list-none p-0 [counter-reset:toc]">
      {items.map((it) => (
        <li key={it.id} className="[counter-increment:toc] border-b border-line last:border-b-0">
          <a
            href={`#${it.id}`}
            className="grid grid-cols-[26px_1fr] items-baseline gap-2.5 py-2.5 text-[14px] leading-[1.35] text-ink-2 no-underline transition-colors hover:text-yellow-04 before:font-display before:text-[11px] before:font-bold before:text-muted before:[content:counter(toc,decimal-leading-zero)] hover:before:text-yellow-04"
          >
            {it.label}
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export const DocShell = ({ toc, children }) => (
  <div className="mx-auto grid max-w-[1080px] grid-cols-1 items-start gap-16 px-9 pb-[88px] pt-14 lg:grid-cols-[248px_1fr]">
    {toc}
    <div className="min-w-0">{children}</div>
  </div>
);

export const IntroNote = ({ children }) => (
  <p className="mb-[52px] font-sans text-[18px] leading-[1.6] text-ink-2">{children}</p>
);

export const DocSection = ({ index, title, id, children }) => (
  <section id={id} className="mb-14 scroll-mt-6">
    <div className="mb-[22px] grid grid-cols-[auto_1fr] items-baseline gap-4 border-b border-line pb-[18px]">
      <span className="pt-[9px] font-display text-[13px] font-extrabold tracking-[0.04em] text-yellow-04">
        {pad(index)}
      </span>
      <h2 className="m-0 font-sans font-extrabold tracking-[-0.02em] text-ink-strong text-[22px] sm:text-[28px] leading-[1.15]">
        {title}
      </h2>
    </div>
    {children}
  </section>
);

export const P = ({ children }) => (
  <p className="mb-4 max-w-[64ch] text-[16px] leading-[1.72] text-ink-3">{children}</p>
);

export const Subsection = ({ title, children }) => (
  <div className="mt-6 border-l-2 border-brand pb-1 pl-[26px] pt-[22px]">
    <h3 className="mb-3 font-display text-[17px] font-bold tracking-[-0.005em] text-ink">{title}</h3>
    {children}
  </div>
);

export const DocList = ({ items }) => (
  <ul className="my-[6px] mb-4 list-none p-0">
    {items.map((it) => (
      <li
        key={typeof it === 'string' ? it : it.key}
        className="relative mb-[11px] max-w-[62ch] pl-6 text-[16px] leading-[1.65] text-ink-3 before:absolute before:left-1 before:top-[11px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-brand before:content-['']"
      >
        {it}
      </li>
    ))}
  </ul>
);

export const ClosingNote = ({ children }) => (
  <p className="mt-1 text-[14.5px] italic text-muted">{children}</p>
);
